import { useCallback, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  AiStarFilled,
  Download,
  Edit,
  OverflowMenuVertical,
  Share,
  StatusChange,
  TrashCan,
} from '@mint-lib/icons';
import { useProfile } from '@mint-lib/profile-helpers';
import { useFlags } from '@mint-lib/routing-context';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import { useAiHubPermissions } from '../../../../hooks/useAiHubPermissions.jsx';
import type { RagTableType } from '../../../../types/rag.js';
import { useDocumentActions } from '../../useDocumentActions.js';

type Props = {
  onOpenDeleteDialog: (document: RagTableType[]) => void;
  onUnselectTableRows: () => void;
  data?: RagTableType;
  className?: string;
  onRename?: () => void;
  onOpenEditPermissionsDialog: (document: RagTableType) => void;
  onOpenMoveDialog?: () => void;
};

const ActionsCell = ({
  data,
  onOpenDeleteDialog,
  onUnselectTableRows,
  onRename,
  onOpenEditPermissionsDialog,
  onOpenMoveDialog,
  className,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const ff = useFlags('@mint/arm', ['aiFileSystem']);
  const { onDocumentDownload } = useDocumentActions();
  const { canDeleteKBFile, canDownloadKBFile } = useAiHubPermissions();
  const { profileService } = useProfile();
  const [open, setOpen] = useState(false);

  // Only the uploader manages who else may see the file, so files owned by
  // somebody else keep the plain download/delete menu.
  const profileId = profileService.getProfileData()?.id;
  const isOwnFile = Boolean(data && profileId === data.userId);
  const canEditPermissions = ff.aiFileSystem.enabled && isOwnFile;

  const handleDelete = useCallback(() => {
    if (!data) return;
    onOpenDeleteDialog([data]);
    setOpen(false);
  }, [data, onOpenDeleteDialog]);

  const handleDownload = () => {
    if (!data) return;

    onDocumentDownload(data);
    onUnselectTableRows();
    setOpen(false);
  };

  const handleEditPermissions = () => {
    if (!data) return;

    onOpenEditPermissionsDialog(data);
    setOpen(false);
  };

  if (!(canDeleteKBFile || canDownloadKBFile)) return;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          icon
          size={'small'}
          variant={'plain'}
          aria-label="actions-button"
          className={cn(
            'flex h-full min-w-6 flex-row items-center justify-center',
            className,
          )}
          onClick={() => setOpen(true)}
        >
          <OverflowMenuVertical />
        </Button>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          side="bottom"
          sideOffset={5}
          align="end"
          className="relative z-popover h-full w-max max-w-64 min-w-28.5 [scrollbar-width:none] overflow-y-auto scroll-auto rounded-lg data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0"
        >
          {ff.aiFileSystem.enabled && (
            <Button
              size={'small'}
              variant={'text'}
              className="h-10 w-full items-center justify-start gap-2 pl-3"
              // onClick={askMintIq}
              aria-label="ask-mintiq-file"
              onDoubleClick={(e) => e.stopPropagation()}
            >
              <AiStarFilled className="text-text-primary" />
              <p
                className={typographyVariants({
                  variant: 'bodyShort01',
                  color: 'primary',
                })}
              >
                {t('Ask MintIQ')}
              </p>
            </Button>
          )}
          {ff.aiFileSystem.enabled && (
            <Button
              size={'small'}
              variant={'text'}
              className="h-10 w-full items-center justify-start gap-2 pl-3"
              onClick={onOpenMoveDialog}
            >
              <StatusChange className="fill-text-primary" />
              <p
                className={typographyVariants({
                  variant: 'bodyShort01',
                  color: 'primary',
                })}
              >
                {t('Move to')}
              </p>
            </Button>
          )}
          <Button
            size={'small'}
            variant={'text'}
            className="h-10 w-full items-center justify-start gap-2 pl-3"
            aria-label="download-file"
            onClick={() => handleDownload()}
            disabled={!canDownloadKBFile}
          >
            <Download className="fill-text-primary" />
            <p
              className={typographyVariants({
                variant: 'bodyShort01',
                color: 'primary',
              })}
            >
              {t('Download')}
            </p>
          </Button>
          {canEditPermissions && (
            <Button
              size={'small'}
              variant={'text'}
              className="h-10 w-full items-center justify-start gap-2 pl-3"
              aria-label="edit-permissions"
              onClick={handleEditPermissions}
            >
              <Share className="fill-text-primary" />
              <p
                className={typographyVariants({
                  variant: 'bodyShort01',
                  color: 'primary',
                })}
              >
                {t('Edit permissions')}
              </p>
            </Button>
          )}
          {ff.aiFileSystem.enabled && (
            <Button
              icon
              size={'small'}
              variant={'text'}
              className="h-10 w-full items-center justify-start gap-2 pl-3"
              aria-label="rename-file"
              onClick={onRename}
            >
              <Edit className="fill-text-primary" />
              <p
                className={typographyVariants({
                  variant: 'bodyShort01',
                  color: 'primary',
                })}
              >
                {t('Rename')}
              </p>
            </Button>
          )}
          <Button
            icon
            size={'small'}
            variant={'text'}
            className="h-10 w-full items-center justify-start gap-2 pl-3"
            aria-label="delete-file"
            onClick={() => handleDelete()}
            disabled={!canDeleteKBFile}
          >
            <TrashCan className="fill-error-dark" />
            <p
              className={cn(
                typographyVariants({
                  variant: 'bodyShort01',
                }),
                'text-error-dark',
              )}
            >
              {t('Delete')}
            </p>
          </Button>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};

export { ActionsCell };
